/**
 * Copyright 2026 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import { type NextApiRequest } from 'next';
import { type PageContextType } from '@/next-types';
import { type ParsedUrlQuery } from 'querystring';
import { getLocaleForProxiedHostname, getProxiedHostname } from '@/utils/getInitialAppState';
import { getCMSLocale } from '../../utils/getCMSLocale';
import { type Locale } from '@/types/i18n';
import logger from '@/utils/logger';
import type { EnhancedPDPGraphQLResponse, GraphQLResponse } from './enhancedPdpContentTypes';
import { buildEnhancedPdpContentQuery } from './enhancedPdpContentQuery';

export type GetContentByContentTypeGraphQLOptions = {
  brand: string;
  sku?: string;
  taxonomy?: string;
  req: NextApiRequest | PageContextType['req'];
  query: ParsedUrlQuery;
  languageOverride?: string | null;
};

/**
 * Fetches enhanced_pdp_content using Contentstack GraphQL API
 * Uses comprehensive query to fetch full content with all modular blocks
 * Supports 4 specificity levels: brandAndSku, brandAndTaxonomy, brandOnly, taxonomyOnly
 */
export async function getContentByContentTypeGraphQL({
  brand,
  sku,
  taxonomy,
  req,
  languageOverride,
}: GetContentByContentTypeGraphQLOptions): Promise<EnhancedPDPGraphQLResponse> {
  const proxiedHostname = getProxiedHostname(req);
  const language = getCMSLocale(
    (languageOverride as Locale | undefined) || getLocaleForProxiedHostname(proxiedHostname),
  );

  // Prepare fetch request using environment variables
  const apiKey = process.env.CONTENTSTACK_API_KEY!;
  const deliveryToken = process.env.CONTENTSTACK_DELIVERY_TOKEN!;
  const environment = process.env.CONTENTSTACK_ENVIRONMENT!;
  const branch = process.env.CONTENTSTACK_BRANCH || 'main';
  const graphqlApiHost = process.env.CONTENTSTACK_GRAPHQL_API_HOST!;

  if (!apiKey || !deliveryToken || !environment || !graphqlApiHost) {
    logger.error({
      message: 'Missing Contentstack GraphQL configuration',
      meta: {
        hasApiKey: !!apiKey,
        hasDeliveryToken: !!deliveryToken,
        hasEnvironment: !!environment,
        hasGraphqlApiHost: !!graphqlApiHost,
      },
    });
    throw new Error('Missing Contentstack GraphQL configuration');
  }

  // Ensure the URL has the proper protocol
  const baseUrl = graphqlApiHost.startsWith('http') ? graphqlApiHost : `https://${graphqlApiHost}`;
  const graphqlEndpoint = `${baseUrl}/stacks/${apiKey}?environment=${environment}`;

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    api_key: apiKey,
    access_token: deliveryToken,
    branch,
  };

  // Map variables: brand → brandname, sku → skuid (as array)
  const variables = {
    locale: language,
    brandname: brand,
    skuid: sku ? [sku] : [],
    taxonomy: taxonomy || '',
  };

  // Build GraphQL query with variables
  const graphqlQuery = buildEnhancedPdpContentQuery(variables);

  try {
    const response = await fetch(graphqlEndpoint, {
      method: 'POST',
      headers,
      signal: AbortSignal.timeout(
        process.env.CONTENTSTACK_TIMEOUT ? Number(process.env.CONTENTSTACK_TIMEOUT) : 6000,
      ),
      body: JSON.stringify({
        query: graphqlQuery,
        variables,
      }),
    });

    if (!response.ok) {
      const errorText = await response.text();
      logger.error({
        message: 'GraphQL request failed',
        meta: {
          status: response.status,
          statusText: response.statusText,
          error: errorText,
          brand,
          sku,
          taxonomy,
        },
      });
      throw new Error(`GraphQL request failed: ${response.status} ${response.statusText}`);
    }

    const result = (await response.json()) as GraphQLResponse;

    if (result.errors && result.errors.length > 0) {
      logger.error({
        message: 'GraphQL query returned errors',
        meta: {
          errors: result.errors,
          brand,
          sku,
          taxonomy,
        },
      });
      throw new Error(`GraphQL query errors: ${JSON.stringify(result.errors)}`);
    }

    return result.data;
  } catch (err: unknown) {
    logger.error({
      message: 'Failed to fetch enhanced_pdp content via GraphQL',
      meta: {
        error: err,
        brand,
        sku,
        taxonomy,
        language,
      },
    });
    throw new Error('Failed to fetch enhanced_pdp content via GraphQL', { cause: err });
  }
}
